import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from '@mui/material/Tooltip';

import { LAYER_NORM_NONE, NORMS } from '../../constants/layers';
import { DXFEntity } from '../new-project/dxf/types';
import { LightingStandardsInfo } from '../project-settings/components/LayerSidebar/LightingStandardsInfo';
import { LayerTypeIcon } from '../project-settings/components/LayerTypeIcon';
import { LAYER_TYPES } from '../project-settings/types';
import { CadPolygon } from './types';

interface CadPolygonSidebarProps {
  projectName: string;
  onProjectNameChange: (name: string) => void;
  polygons: CadPolygon[];
  activePolygonIndex: number | null;
  drawingPointsCount: number;
  isValid: boolean;
  onUpdatePolygon: (id: string, updates: Partial<CadPolygon>) => void;
  onDeletePolygon: (id: string) => void;
  onSelectPolygon: (index: number | null) => void;
  onCreateProject: () => void;
  onBack: () => void;
  isCreating: boolean;
  importedLayers: string[] | null;
  importedEntities: DXFEntity[] | null;
  onCreatePolygonsFromLayer: (layerName: string) => void;
  onLayerHover: (layerName: string | null) => void;
}

const LAYER_TYPE_KEYS = Object.keys(LAYER_TYPES) as Array<keyof typeof LAYER_TYPES>;

export function CadPolygonSidebar({
  projectName,
  onProjectNameChange,
  polygons,
  activePolygonIndex,
  drawingPointsCount,
  isValid,
  onUpdatePolygon,
  onDeletePolygon,
  onSelectPolygon,
  onCreateProject,
  onBack,
  isCreating,
  importedLayers,
  importedEntities,
  onCreatePolygonsFromLayer,
  onLayerHover,
}: CadPolygonSidebarProps) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameDraft, setEditingNameDraft] = useState<string>('');
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [layersSectionOpen, setLayersSectionOpen] = useState(true);
  const [polygonsSectionOpen, setPolygonsSectionOpen] = useState(true);
  const [undefinedSectionOpen, setUndefinedSectionOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Local string state for height inputs — allows free typing, commits only valid values
  const [heightInputs, setHeightInputs] = useState<Record<string, string>>({});

  const hasLayers = importedLayers && importedLayers.length > 0;

  const hasIlluminatedWithoutNorm = useMemo(
    () => polygons.some((p) => p.illuminated && (!p.norm || p.norm === LAYER_NORM_NONE)),
    [polygons],
  );

  // Stable display order of polygon IDs. Re-sorts only when:
  //   - the set of polygons changes (add/remove)
  //   - editing finishes (editingNameId becomes null)
  // While a polygon is being renamed, order stays frozen so rows don't "dance" as the user types.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  useEffect(() => {
    const currentIds = polygons.map((p) => p.id);
    const currentIdSet = new Set(currentIds);

    if (editingNameId !== null) {
      // During editing: keep existing order, only sync add/remove
      setOrderedIds((prev) => {
        const prevSet = new Set(prev);
        const filtered = prev.filter((id) => currentIdSet.has(id)); // drop deleted
        const added = currentIds.filter((id) => !prevSet.has(id)); // append new
        return [...filtered, ...added];
      });
      return;
    }

    // Not editing: re-sort with natural numeric order so "2" comes before "11"
    const sorted = [...polygons]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
      .map((p) => p.id);
    setOrderedIds(sorted);
  }, [polygons, editingNameId]);

  const sortedPolygons = useMemo(() => {
    const byId = new Map(polygons.map((p, i) => [p.id, { polygon: p, originalIndex: i }]));
    return orderedIds.map((id) => byId.get(id)).filter((x): x is { polygon: CadPolygon; originalIndex: number } => !!x);
  }, [orderedIds, polygons]);

  // Count closed entities per layer
  const layerCounts = useMemo(() => {
    if (!importedEntities) return {};
    const counts: Record<string, number> = {};
    for (const e of importedEntities) {
      if (e.closed && e.vertices.length >= 3) {
        counts[e.layer] = (counts[e.layer] || 0) + 1;
      }
    }
    return counts;
  }, [importedEntities]);

  // Separate layers into usable (have polygons) and undefined (no polygons)
  const { usableLayers, undefinedLayers } = useMemo(() => {
    if (!importedLayers) return { usableLayers: [], undefinedLayers: [] };
    const usable: string[] = [];
    const undef: string[] = [];
    for (const name of importedLayers) {
      if ((layerCounts[name] ?? 0) > 0) {
        usable.push(name);
      } else {
        undef.push(name);
      }
    }
    return { usableLayers: usable, undefinedLayers: undef };
  }, [importedLayers, layerCounts]);

  const handleRowClick = (index: number, polygon: CadPolygon) => {
    onSelectPolygon(index);
    setExpandedId((prev) => (prev === polygon.id ? null : polygon.id));
    setExpandedTypeId(null);
  };

  const handleTypeClick = (e: React.MouseEvent, polygon: CadPolygon, index: number) => {
    e.stopPropagation();
    onSelectPolygon(index);
    if (expandedTypeId === polygon.id) {
      setExpandedTypeId(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      // Estimated dropdown height: header (~30px) + LAYER_TYPE_KEYS rows (~32px each) + padding.
      const dropdownHeight = 30 + LAYER_TYPE_KEYS.length * 32 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < dropdownHeight + 8) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
      } else {
        setDropdownPos({ top: rect.bottom + 4, left: rect.left });
      }
      setExpandedTypeId(polygon.id);
    }
  };

  const handleTypeSelect = (polygonId: string, type: keyof typeof LAYER_TYPES) => {
    onUpdatePolygon(polygonId, { type });
    setExpandedTypeId(null);
  };

  const handleNormSelect = (polygonId: string, norm: string) => {
    onUpdatePolygon(polygonId, { norm });
    setExpandedId(null);
  };

  const handleNameDoubleClick = (e: React.MouseEvent, polygonId: string) => {
    e.stopPropagation();
    const current = polygons.find((p) => p.id === polygonId);
    setEditingNameDraft(current?.name ?? '');
    setEditingNameId(polygonId);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  // Commit draft on blur/Enter. Empty names are not allowed — revert to previous name.
  const commitNameEdit = (polygonId: string) => {
    const trimmed = editingNameDraft.trim();
    if (trimmed.length > 0) {
      onUpdatePolygon(polygonId, { name: trimmed });
    }
    setEditingNameId(null);
    setEditingNameDraft('');
  };

  const cancelNameEdit = () => {
    setEditingNameId(null);
    setEditingNameDraft('');
  };

  const handleLayerCreate = (layerName: string) => {
    const count = layerCounts[layerName] ?? 0;
    if (count === 0) return;
    onCreatePolygonsFromLayer(layerName);
  };

  return (
    <div className="fixed top-[64px] bottom-0 right-0 h-[calc(100vh-64px)] w-full md:w-[455px] md:rounded-l-2xl bg-[#232323] text-gray-300 flex flex-col z-10 shadow-2xl pointer-events-auto">
      {/* Header — Project Name */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-800">
        <input
          type="text"
          placeholder={t('projects.newProject.projectNamePlaceholder')}
          value={projectName}
          onChange={(e) => onProjectNameChange(e.target.value)}
          className="w-full h-[36px] rounded-lg bg-black border border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30 placeholder-[#747474] text-sm px-4 text-white transition outline-none"
        />
      </div>

      {/* Scrollable content: Layers + Polygons */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {/* ── Layers Section ─────────────────────────────────────────────── */}
        {hasLayers && (
          <div className="border-b border-[#313131]">
            {/* Section header */}
            <button
              onClick={() => setLayersSectionOpen(!layersSectionOpen)}
              className="w-full pl-4 pr-6 py-2.5 flex items-center justify-between hover:bg-[#2a2a2a] transition"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-3 h-3 text-gray-500 transition-transform ${layersSectionOpen ? 'rotate-90' : ''}`}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5l8 7-8 7z" />
                </svg>
                <svg
                  className="w-4 h-4 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t('projects.cadProject.layers', 'Layers')}
                </span>
                <span className="text-[10px] text-gray-600">({importedLayers!.length})</span>
              </div>
            </button>

            {/* Description / disclaimer */}
            {layersSectionOpen && (
              <div className="px-4 pb-2 space-y-1">
                <p className="text-[12px] text-red-500/80 leading-relaxed">
                  {t('projects.cadProjectLegacy.layersDisclaimer')}
                </p>
                <p className="text-[12px] text-gray-500 leading-relaxed">
                  {t('projects.cadProjectLegacy.layersHowTo')}
                </p>
              </div>
            )}

            {/* Usable layer rows */}
            {layersSectionOpen && (
              <div className="divide-y divide-[#2a2a2a] max-h-[30vh] overflow-y-auto">
                {usableLayers.map((layerName) => {
                  const closedCount = layerCounts[layerName] ?? 0;

                  return (
                    <div
                      key={layerName}
                      onMouseEnter={() => onLayerHover(layerName)}
                      onMouseLeave={() => onLayerHover(null)}
                      className="h-[36px] pl-8 pr-4 flex items-center justify-between transition hover:bg-[#313131]"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <svg
                          className="w-3.5 h-3.5 shrink-0 text-gray-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polygon points="12 2 2 7 12 12 22 7 12 2" />
                        </svg>
                        <span className="text-sm text-gray-200 truncate">{layerName}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-lime-400 bg-lime-400/10 px-1.5 py-0.5 rounded">
                          {closedCount}
                        </span>
                        <button
                          onClick={() => handleLayerCreate(layerName)}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-black bg-lime-400 rounded hover:bg-lime-500 transition"
                        >
                          <svg
                            className="w-2.5 h-2.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                          {t('projects.cadProject.createBtn', 'Create')}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Undefined layers group */}
                {undefinedLayers.length > 0 && (
                  <>
                    <button
                      onClick={() => setUndefinedSectionOpen(!undefinedSectionOpen)}
                      className="w-full h-[36px] pl-8 pr-6 flex items-center justify-between hover:bg-[#2a2a2a] transition"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <svg
                          className={`w-2.5 h-2.5 text-gray-600 transition-transform ${undefinedSectionOpen ? 'rotate-90' : ''}`}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5l8 7-8 7z" />
                        </svg>
                        <span className="text-xs text-gray-500 italic">
                          {t('projects.cadProject.undefinedLayers', 'Undefined')}
                        </span>
                        <span className="text-[10px] text-gray-600">({undefinedLayers.length})</span>
                      </div>
                    </button>
                    {undefinedSectionOpen &&
                      undefinedLayers.map((layerName) => (
                        <div
                          key={layerName}
                          onMouseEnter={() => onLayerHover(layerName)}
                          onMouseLeave={() => onLayerHover(null)}
                          className="h-[32px] pl-12 pr-6 flex items-center justify-between transition opacity-40"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <svg
                              className="w-3 h-3 shrink-0 text-gray-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polygon points="12 2 2 7 12 12 22 7 12 2" />
                            </svg>
                            <span className="text-xs text-gray-500 truncate">{layerName}</span>
                          </div>
                          <span className="text-[10px] text-gray-600">—</span>
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Polygons Section ───────────────────────────────────────────── */}
        <div className="border-b border-[#313131]">
          {/* Section header */}
          <button
            onClick={() => setPolygonsSectionOpen(!polygonsSectionOpen)}
            className="w-full pl-4 pr-6 py-2.5 flex items-center justify-between hover:bg-[#2a2a2a] transition"
          >
            <div className="flex items-center gap-2">
              <svg
                className={`w-3 h-3 text-gray-500 transition-transform ${polygonsSectionOpen ? 'rotate-90' : ''}`}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5l8 7-8 7z" />
              </svg>
              <svg
                className="w-4 h-4 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
              </svg>
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('projects.cadProject.polygons', 'Polygons')}
              </span>
              <span className="text-[10px] text-gray-600">({polygons.length})</span>
            </div>
          </button>

          {polygonsSectionOpen && (
            <>
              {/* Column Headers */}
              <div className="pl-4 pr-6 py-2 flex items-center justify-between border-b border-[#313131] flex-shrink-0 sticky top-0 bg-[#232323] z-[1]">
                <div className="flex items-center gap-3">
                  <Tooltip title={t('workspace.typeTooltip', 'Type of layer (ex. Road, Sidewalk)')} arrow>
                    <span className="text-[10px] text-gray-500 w-6 text-center cursor-help">{t('workspace.type')}</span>
                  </Tooltip>
                  <span className="text-[10px] text-gray-500">{t('common.name')}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Tooltip title={t('projects.cadProject.heightTooltip', 'Height of 3D object in scene')} arrow>
                    <span className="text-[10px] text-gray-500 w-12 text-center cursor-help">H (m)</span>
                  </Tooltip>
                  <Tooltip title={t('workspace.lmnTooltip')} arrow>
                    <span className="text-[10px] text-gray-500 w-9 text-center cursor-help">{t('workspace.lmn')}</span>
                  </Tooltip>
                  <Tooltip title={t('workspace.normTooltip', 'Lighting standard applied to illuminated layer')} arrow>
                    <span className="text-[10px] text-gray-500 w-[50px] text-center cursor-help">
                      {t('workspace.norm')}
                    </span>
                  </Tooltip>
                  <span className="text-[10px] text-gray-500 w-6" />
                </div>
              </div>

              <div className="divide-y divide-[#313131]">
                {/* Empty state */}
                {polygons.length === 0 && drawingPointsCount === 0 && (
                  <div className="text-center py-12 text-gray-600 text-sm">{t('projects.cadProject.noPolygons')}</div>
                )}

                {/* Polygon rows */}
                {sortedPolygons.map(({ polygon: p, originalIndex: i }) => {
                  const isActive = i === activePolygonIndex;
                  const isExpanded = expandedId === p.id;
                  const isEditingName = editingNameId === p.id;

                  return (
                    <React.Fragment key={p.id}>
                      {/* Row */}
                      <div
                        onClick={() => handleRowClick(i, p)}
                        className={`h-[36px] pl-4 pr-6 flex items-center justify-between hover:bg-[#313131] transition cursor-pointer ${
                          isActive ? 'bg-lime-400/10' : ''
                        }`}
                      >
                        {/* Left: Type + Color + Name */}
                        <div className="flex items-center gap-3 overflow-hidden mr-4">
                          {/* Type icon */}
                          <div className="relative">
                            <button
                              onClick={(e) => handleTypeClick(e, p, i)}
                              className="w-6 h-6 flex items-center justify-center border border-black bg-transparent rounded hover:border-gray-500 transition shrink-0"
                              title={`Type: ${p.type}`}
                            >
                              <LayerTypeIcon
                                type={p.type as keyof typeof LAYER_TYPES}
                                style={{ width: 14, height: 14, color: 'white' }}
                              />
                            </button>
                          </div>

                          {/* Name */}
                          {isEditingName ? (
                            <input
                              ref={nameInputRef}
                              type="text"
                              value={editingNameDraft}
                              onChange={(e) => setEditingNameDraft(e.target.value)}
                              onBlur={() => commitNameEdit(p.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitNameEdit(p.id);
                                else if (e.key === 'Escape') cancelNameEdit();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 text-sm bg-black border border-lime-500 rounded px-1 text-white outline-none"
                              autoFocus
                            />
                          ) : (
                            <span
                              className="font-medium text-gray-100 truncate pl-1 cursor-text"
                              style={{ fontSize: '14px' }}
                              title={p.name}
                              onDoubleClick={(e) => handleNameDoubleClick(e, p.id)}
                            >
                              {p.name}
                            </span>
                          )}
                        </div>

                        {/* Right: Height + LMN + Norm + Delete */}
                        <div className="flex items-center gap-4 shrink-0">
                          {/* Height input */}
                          <Tooltip title={t('projects.cadProject.heightTooltip', 'Height of 3D object in scene')} arrow>
                            <input
                              type="number"
                              min={0}
                              step={0.5}
                              value={heightInputs[p.id] ?? p.height}
                              onChange={(e) => {
                                e.stopPropagation();
                                const raw = e.target.value;
                                setHeightInputs((prev) => ({ ...prev, [p.id]: raw }));
                                const num = parseFloat(raw);
                                if (!isNaN(num) && num >= 0) {
                                  onUpdatePolygon(p.id, { height: num });
                                }
                              }}
                              onBlur={() => {
                                const raw = heightInputs[p.id];
                                if (raw === undefined) return;
                                const num = parseFloat(raw);
                                if (isNaN(num) || num < 0) {
                                  // Reset to polygon's current value
                                  setHeightInputs((prev) => {
                                    const next = { ...prev };
                                    delete next[p.id];
                                    return next;
                                  });
                                } else {
                                  setHeightInputs((prev) => {
                                    const next = { ...prev };
                                    delete next[p.id];
                                    return next;
                                  });
                                }
                              }}
                              onFocus={() => onSelectPolygon(i)}
                              onClick={(e) => e.stopPropagation()}
                              className={`w-12 h-6 px-1 rounded border bg-black text-green01 text-xs outline-none text-right tabular-nums ${
                                heightInputs[p.id] !== undefined &&
                                (isNaN(parseFloat(heightInputs[p.id])) || parseFloat(heightInputs[p.id]) < 0)
                                  ? 'border-red-500'
                                  : 'border-white/10 focus:border-green01'
                              }`}
                            />
                          </Tooltip>

                          {/* LMN toggle */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectPolygon(i);
                              const newIllum = !p.illuminated;
                              onUpdatePolygon(p.id, {
                                illuminated: newIllum,
                                ...(newIllum ? {} : { norm: LAYER_NORM_NONE }),
                              });
                            }}
                            className="relative w-9 h-5 rounded-full bg-black transition-colors duration-200 ease-in-out focus:outline-none"
                          >
                            <div
                              className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                                p.illuminated ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
                              }`}
                            />
                          </button>

                          {/* Norm */}
                          <div
                            className={`w-[50px] h-[28px] flex items-center justify-center text-sm ${
                              p.illuminated ? '' : 'opacity-50'
                            }`}
                          >
                            {p.norm && p.norm !== LAYER_NORM_NONE ? (
                              <span className="text-[#B3EC49]">{p.norm}</span>
                            ) : (
                              <span className="text-gray-500">{t('common.none')}</span>
                            )}
                          </div>

                          {/* Delete */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePolygon(p.id);
                            }}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-400/20 text-gray-500 hover:text-red-400 transition shrink-0"
                            title={t('common.delete')}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Expanded section */}
                      {isExpanded && (
                        <div className="bg-[#313131] shadow-inner">
                          {/* Norm selector (only when illuminated) */}
                          {p.illuminated && (
                            <div className="px-6 py-4 border-b border-[#2a2a2a]">
                              <div className="flex justify-between items-center mb-2">
                                <p className="text-xs text-gray-500">{t('projects.newProject.chooseNorm')}</p>
                                <LightingStandardsInfo />
                              </div>
                              <div className="grid grid-cols-6 gap-1.5">
                                {NORMS.map((norm) => (
                                  <button
                                    key={norm}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNormSelect(p.id, norm);
                                    }}
                                    style={{ height: '28px', fontSize: '12px' }}
                                    className={`px-1 rounded font-medium transition ${norm === LAYER_NORM_NONE ? 'col-span-2' : ''} ${
                                      p.norm === norm
                                        ? 'bg-lime-500 text-black'
                                        : 'bg-[#232323] text-white hover:bg-lime-500 hover:text-black'
                                    }`}
                                  >
                                    {norm}
                                  </button>
                                ))}
                              </div>
                              {p.norm === LAYER_NORM_NONE && (
                                <p className="text-[10px] text-red-400 mt-1.5">
                                  {t('projects.cadProject.normRequired')}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Vertex coordinates list */}
                          <div className="px-6 py-3">
                            <p className="text-xs text-gray-500 mb-2">
                              {t('projects.cadProject.vertices', 'Vertices')} ({p.points.length})
                            </p>
                            <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                              <div className="grid grid-cols-2 gap-1">
                                {p.points.map(([x, y], vi) => (
                                  <div key={vi} className="rounded border border-white/5 bg-black/30 px-1.5 py-1">
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-[9px] text-gray-500 font-medium">#{vi + 1}</span>
                                      {p.points.length > 3 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newPoints = p.points.filter((_, i) => i !== vi);
                                            onUpdatePolygon(p.id, { points: newPoints });
                                          }}
                                          className="w-3.5 h-3.5 flex items-center justify-center rounded text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                          title={t('common.delete')}
                                        >
                                          <svg
                                            className="w-2.5 h-2.5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                      <span className="text-[9px] text-red-400 shrink-0">X</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        value={parseFloat(x.toFixed(2))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          const newPoints = [...p.points] as [number, number][];
                                          newPoints[vi] = [parseFloat(e.target.value) || 0, y];
                                          onUpdatePolygon(p.id, { points: newPoints });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex-1 min-w-0 h-4 px-0.5 rounded border border-white/10 bg-black text-white text-[10px] outline-none text-right tabular-nums focus:border-lime-400"
                                      />
                                    </div>
                                    <div className="flex items-center gap-0.5 mt-0.5">
                                      <span className="text-[9px] text-green-400 shrink-0">Y</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        value={parseFloat(y.toFixed(2))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          const newPoints = [...p.points] as [number, number][];
                                          newPoints[vi] = [x, parseFloat(e.target.value) || 0];
                                          onUpdatePolygon(p.id, { points: newPoints });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex-1 min-w-0 h-4 px-0.5 rounded border border-white/10 bg-black text-white text-[10px] outline-none text-right tabular-nums focus:border-lime-400"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawing indicator — fixed outside scroll so it's always visible while drawing */}
      {drawingPointsCount > 0 && (
        <div className="flex-shrink-0 px-4 py-2 bg-[#0c1926] border-t border-l-[3px] border-l-blue-500 border-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs text-blue-400">
              {t('projects.cadProject.drawingInProgress', { count: drawingPointsCount })}
            </span>
          </div>
        </div>
      )}

      {/* Validation message */}
      {hasIlluminatedWithoutNorm && (
        <div className="flex-shrink-0 px-6 py-2 bg-[#232323] border-t border-gray-800">
          <p className="text-[11px] text-red-400 text-center">
            {t('projects.newProject.validation.illuminatedNormRequired')}
          </p>
        </div>
      )}

      {/* Bottom bar */}
      <div
        className={`flex-shrink-0 rounded-bl-2xl bg-[#232323] w-full h-[60px] flex justify-between px-6 items-center gap-4 ${hasIlluminatedWithoutNorm ? '' : 'border-t border-gray-800'}`}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-between rounded-full font-medium transition-colors duration-200 text-xs border border-[#B3EC49] text-[#B3EC49] hover:border-lime-500"
          style={{
            height: '36px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '18px',
            paddingRight: '18px',
            gap: '8px',
          }}
        >
          {t('common.back')}
        </button>
        <button
          onClick={onCreateProject}
          disabled={!isValid || isCreating}
          className="flex items-center rounded-full font-medium transition-colors duration-200 text-xs bg-lime-400 text-black hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            height: '36px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '18px',
            paddingRight: '18px',
            gap: '8px',
          }}
        >
          {isCreating ? t('projects.cadProject.creating') : t('projects.cadProject.createProject')}
        </button>
      </div>

      {/* Type dropdown — rendered outside scroll container */}
      {expandedTypeId && (
        <div className="fixed inset-0 z-40" onClick={() => setExpandedTypeId(null)}>
          <div
            className="fixed bg-[#232323] rounded-md shadow-lg overflow-hidden min-w-[180px] z-50"
            style={{
              left: dropdownPos.left,
              ...(dropdownPos.bottom !== undefined ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 bg-[#313131] max-h-[30px]">
              <span className="text-xs font-medium text-white">{t('projects.newProject.setLayerType')}</span>
            </div>
            <div className="py-1">
              {LAYER_TYPE_KEYS.map((type) => {
                const current = polygons.find((pp) => pp.id === expandedTypeId);
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect(expandedTypeId, type);
                    }}
                    className={`layer-icon-container w-full flex items-center gap-2 px-3 py-2 text-left transition ${
                      current?.type === type
                        ? 'bg-[#313131] text-white'
                        : 'text-white hover:bg-[#B3EC49] hover:text-black'
                    }`}
                  >
                    <LayerTypeIcon type={type} style={{ width: 14, height: 14 }} />
                    <span className="text-sm">{type}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
